import { type Agency, type MerchantRequest } from '../../../common/types';
import { Form, Formik, type FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import AddMerchantForm from './AddMerchantForm';
import { AddMerchantSchema } from '../merchant.schema';
import CustomSelect from '../../../components/Form/CustomSelect';
import { extractErrorsFromApi } from '../../../common/helpers';
import type { ParentContext } from '../../../common/types/common';
import RenderErrorBag from '../../../components/render-error-bag';
import { toast } from 'react-toastify';
import { useAddMerchantMutation } from '../merchant.api-slice';
import { useAuth } from '../../../app/hooks/useAuth';
import { useLazyGetListOfAgenciesQuery } from '../../agencies/agency.api-slice';
import { UserRole } from '../../../common/constants';
import { useTranslation } from 'react-i18next';

interface IProps {
  isOpen: boolean;
  toggleModal: () => any;
  parentContext: ParentContext;
  agencyInContext?: Agency;
}

const initialData: MerchantRequest = {
  name: 'Merchant X',
  email: 'merx@example.com',
  phone: '123456',
  address: '123456'
};

interface Option {
  label: string;
  value: string;
}

const AddMerchant: React.FC<IProps> = ({ isOpen, toggleModal, agencyInContext, parentContext }) => {
  const [selectedAgency, setSelectedAgency] = useState<Option | undefined>(
    agencyInContext != null ? { value: agencyInContext.id, label: agencyInContext.name } : undefined
  );
  const [listOfAgencies, setListOfAgencies] = useState<Option[]>([]);
  const [addItem, { isLoading, isError, error, isSuccess, reset }] = useAddMerchantMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const { t } = useTranslation();
  const { user } = useAuth();
  const [trigger, result] = useLazyGetListOfAgenciesQuery();

  const handleAddItem = async (values: MerchantRequest, actions: FormikHelpers<MerchantRequest>): Promise<any> => {
    await addItem({ ...values, agencyId: selectedAgency?.value ?? parentContext.agencyId });
    actions.resetForm();
  };

  const handleAgencyChange = (selectedValue: any): void => {
    setSelectedAgency(selectedValue);
  };

  useEffect(() => {
    if (isSuccess) {
      toast(t('merchants.add.successMessage'));
      reset();
      toggleModal();
    }
    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [isError, isSuccess]);

  useEffect(() => {
    if (user?.role === UserRole.SUPER_ADMIN && isOpen) {
      void trigger();
    }
  }, [isOpen, user]);

  useEffect(() => {
    if (result?.data != null && result.data.length > 0) {
      setListOfAgencies(
        result.data.map((agency: Agency) => {
          return { label: agency.name, value: agency.id };
        })
      );
    }
  }, [result]);

  return (
    <>
      {isOpen && (
        <>
          <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-full my-6 mx-auto max-w-3xl">
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                <div className="flex items-start justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                  <h3 className="text-3xl font-semibold">{t('merchants.add.title')}</h3>
                </div>
                <div className="relative p-6 flex-auto w-full">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {
                    <Formik initialValues={initialData} onSubmit={handleAddItem} validationSchema={AddMerchantSchema}>
                      {({ isSubmitting }) => (
                        <Form className="w-full flex flex-col items-start px-2" autoComplete="off">
                          {user != null && user.role === UserRole.SUPER_ADMIN && (
                            <CustomSelect
                              label="Agency"
                              name="agencyId"
                              divClass="w-full flex flex-col "
                              labelClass="w-full text-left text-gray-600"
                              inputClass="w-full my-1 px-4 py-1 rounded-md"
                              options={listOfAgencies}
                              defaultValue={selectedAgency}
                              handleChange={handleAgencyChange}
                            />
                          )}
                          <AddMerchantForm />
                          <div className="w-full flex space-x-2 py-6 mt-4 border-t border-solid border-slate-200 rounded-b">
                            <button className="w-1/2 text-black px-5 py-2 rounded-lg bg-gray-200" onClick={toggleModal}>
                              {t('merchants.add.cancelBtnLabel')}
                            </button>
                            <button
                              type="submit"
                              disabled={isLoading || isSubmitting}
                              className="w-1/2 text-white px-4 py-2 rounded-lg bg-[#1D8A43]"
                            >
                              {t('merchants.add.saveBtnLabel')}
                            </button>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
};

export default AddMerchant;
